import { AxiosRequestConfig } from 'axios'
import { ContractResponseDTO, ContractsQueryDTO, LoteQueryDTO } from 'models'
import { ContractAnnexRequestDTO } from 'models/ContractAnnexRequestDTO'
import { ContractRequestDTO } from 'models/ContractRequestDTO'
import { ContractRequestUpdateDTO } from 'models/ContractRequestUpdateDTO'
import { ExternalAdditiveRequestDTO } from 'models/ExternalAdditiveRequestDTO'
import { dismountUpdateContract } from 'pages/Contract/utils/dismountUpdateContract'
import { FormFileDataProps } from 'pages/Contracts/Components/Forms/FormFileData/interfaces'
import { FinalResponse } from 'pages/Contracts/Components/StepsForm/interface'
import { createContext, useContext, useState } from 'react'
import ContractService from 'services/contracts'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import {
    ContractContextData,
    ContractContextProps,
    ContractEdit,
    ContractMode
} from './interface'

export const ContractContext = createContext({} as ContractContextData)

export const useContractContext = () => {
    return useContext(ContractContext)
}

export const ContractProvider = ({ children }: ContractContextProps) => {
    const [loadingContract, setLoadingContract] = useState(true)
    const [loadingBatch, setLoadingBatch] = useState(false)
    const [loadingLote, setLoadingLote] = useState(false)
    const [loadingCreateBatch, setLoadingCreateBatch] = useState(false)

    const [newContract, setNewContract] = useState(false)
    const [typeContractBoolean, setTypeContractBoolean] = useState(false)
    const [typeContract, setTypeContract] = useState<ContractMode>('CREATE')

    const [currentContract, setCurrentContract] =
        useState<ContractResponseDTO>()

    const [currentContractId, setCurrentContractId] =
        useState<ContractResponseDTO>()

    const setContract = (contract: ContractResponseDTO) => {
        setCurrentContract(contract)
    }

    const [fileData, setFileData] = useState({} as FormFileDataProps)
    const [endUploadFile, setEndUploadFile] = useState(false)

    const getContracts = (
        query?: ContractsQueryDTO,
        isBackoffice?: boolean,
        IF?: string
    ) => {
        setLoadingContract(true)
        return ContractService.getContracts(query, isBackoffice, IF).finally(
            () => setLoadingContract(false)
        )
    }

    const getContractType = (id: string) => {
        return ContractService.getContractType(id)
    }

    const getReasonRefusedContract = (id: string) => {
        return ContractService.getReasonRefusedContract(id)
    }

    const getContract = async (id: string) => {
        setLoadingContract(true)
        return await ContractService.getContract(id)
            .then(res => {
                const dataFormated = dismountUpdateContract(res.data)
                setCurrentContractId(
                    removeEmptyValues(
                        dataFormated as unknown as ContractResponseDTO
                    )
                )
                return res
            })
            .finally(() => setLoadingContract(false))
    }

    const editContract = (
        id: string,
        data: ContractRequestUpdateDTO,
        type: ContractEdit
    ) => {
        setLoadingContract(true)
        return ContractService.editContract(
            id,
            removeEmptyValues(data),
            type
        ).finally(() => {
            setLoadingContract(false)
        })
    }

    const createContract = (data: ContractRequestDTO) => {
        setLoadingContract(true)
        const newData = removeEmptyValues(data)
        return ContractService.createContract(newData).finally(() => {
            setLoadingContract(false)
        })
    }

    const createExternalAdditive = (data: ExternalAdditiveRequestDTO) => {
        setLoadingContract(true)
        const newData = removeEmptyValues(data)

        return ContractService.createExternalAdditive(newData).finally(() => {
            setLoadingContract(false)
        })
    }

    const forwardToDetran = (
        data: FinalResponse,
        config?: AxiosRequestConfig<any>
    ) => {
        return ContractService.forwardToDetran(data, config)
    }

    const postAnnex = (id: string, data: ContractAnnexRequestDTO) => {
        setLoadingContract(true)
        return ContractService.postAnnex(id, data).finally(() => {
            setLoadingContract(false)
            setEndUploadFile(true)
        })
    }

    const postBatch = (data: FormData, indexTab: number) => {
        setLoadingCreateBatch(true)
        return ContractService.postBatch(data, indexTab).finally(() => {
            setLoadingCreateBatch(false)
        })
    }

    const deleteAnnex = async (id: string, idAnnex: string) => {
        return ContractService.deleteAnnex(id, idAnnex).finally(() =>
            setLoadingContract(false)
        )
    }

    const getLote = async (
        query?: LoteQueryDTO,
        isBackoffice?: boolean,
        IF?: string,
        indexTab?: number
    ) => {
        setLoadingLote(true)
        return await ContractService.getLote(
            query,
            isBackoffice,
            IF,
            indexTab
        ).finally(() => setLoadingLote(false))
    }

    const downloadAnnex = (id: string, idAnnex: string) => {
        return ContractService.downloadAnnex(id, idAnnex)
    }

    const getLoteFile = async (
        id: string,
        typeFile: 'DOWNLOAD' | 'PREVIEW',
        indexTab?: number
    ) => {
        return await ContractService.getLoteFile(id, typeFile, indexTab)
    }

    const values = {
        currentContract,
        currentContractId,
        loadingContract,
        loadingBatch,
        loadingCreateBatch,
        loadingLote,
        newContract,
        typeContract,
        typeContractBoolean,
        fileData,
        endUploadFile,
        setLoadingLote,
        setFileData,
        setLoadingBatch,
        setTypeContractBoolean,
        setTypeContract,
        setNewContract,
        setEndUploadFile,
        getContracts,
        getContract,
        getContractType,
        editContract,
        createContract,
        createExternalAdditive,
        setContract,
        forwardToDetran,
        postAnnex,
        postBatch,
        deleteAnnex,
        downloadAnnex,
        getLote,
        getLoteFile,
        getReasonRefusedContract
    }
    return (
        <ContractContext.Provider value={values}>
            {children}
        </ContractContext.Provider>
    )
}
